import "./index.css";
import React, { useMemo, useState } from "react";
import StartSetup from "./StartSetup";
import ContactInfo from "./ContactInfo";
import { Button, Form, Spin, message } from "antd";
import PersonalInfo from "./PersonalInfo";
import CompanyDetails from "./CompanyDetails";
import VerifyCredentials from "./VerifyCredentials";
import { useRequest } from "ahooks";
import { getCustomerId } from "Common";
import { track } from "statsig";
import { STATSIG } from "statsig/events";

import ICON_FILLED from "../assets/filled.svg";
import ICON_CONTACT from "../assets/contact.svg";
import ICON_COMPANY from "../assets/company.svg";
import ICON_PERSONAL from "../assets/personal.svg";
import ICON_CREDENTIALS from "../assets/credentials.svg";
import {
  fetchPersonalSiteCompanyOptions,
  resolveBluerateSetupHint,
  type BluerateSetupHint,
} from "../EditPreview/components/PersonalSiteCompanyInfo";
import { switchOrganization } from "../../BranchSwitcher/switchOrganization";

interface TypeProfileConfigProps<T = number | void> {
  /**
   * @name onDone 填写完最后一个表单，并完成
   */
  onChange(value?: T): void;
  /**
   * @param value 当前处于哪个流程
   */
  value: T;
  /**
   * @param loading 是否正在同步数据
   */
  loading?: boolean;
}

/**
 * @name PERSONAL_ITEMS 关键表单组件
 * @description 其中 "公司项 Company details"，会根据返回动态显示，请注意 “/los/getcompanyprofilesetup” API
 */
export const PERSONAL_ITEMS = [
  {
    icon: ICON_PERSONAL,
    title: "Personal info",
    description: "Help borrowers get to know the real you.",
    component: PersonalInfo,
    validate: PersonalInfo.REQUIRED,
  },
  {
    icon: ICON_CONTACT,
    title: "Contact info",
    description: "Make it easy for leads to reach out directly.",
    component: ContactInfo,
    validate: ContactInfo.REQUIRED,
  },
  {
    icon: ICON_CREDENTIALS,
    title: "Verify your credentials",
    description: "Showcase your authority and expertise.",
    component: VerifyCredentials,
    validate: VerifyCredentials.REQUIRED,
  },
  {
    icon: ICON_COMPANY,
    title: "Company details",
    description: "Add credibility by linking to your brokerage.",
    component: CompanyDetails,
    validate: CompanyDetails.REQUIRED,
  },
];

const AVATAR_DEFAULT = `https://zeitro-images.s3.us-west-1.amazonaws.com/bluerate-default-avatar.png`;

async function openBluerateInProfileOrg(hint: BluerateSetupHint) {
  const targetId = hint.profileOrg.customerId;
  if (!targetId) return;
  await switchOrganization(targetId, {
    redirectTo: "/app/landingpage",
    notice: `Switched to ${hint.profileOrgName} — opened its Bluerate settings`,
  });
}

/**
 * @name ProfileConfig 个人信息配置
 * @description
 *  <ProfileConfig/> 仅关注用户初始化配置流程
 *  提供 “Personal info” 、“Contact info” 、“Verify your credentials” 、“Company details” 流程化配置
 */
const ProfileConfig: React.FC<TypeProfileConfigProps> = ({
  loading,
  onChange,
  value = -1,
}) => {
  const form = Form.useFormInstance();
  const [switchingOrg, setSwitchingOrg] = useState(false);
  const companyName = Form.useWatch(["LandingPageSetUp", "CompanyName"], form);
  const { data: companyOptions = [] } = useRequest(fetchPersonalSiteCompanyOptions);
  const currentCustomerId = getCustomerId() || "";
  const bluerateHint = useMemo(
    () =>
      resolveBluerateSetupHint(
        companyOptions,
        currentCustomerId,
        companyName,
      ),
    [companyName, companyOptions, currentCustomerId],
  );

  async function onOpenBluerateProfile() {
    if (!bluerateHint) return;
    try {
      setSwitchingOrg(true);
      await openBluerateInProfileOrg(bluerateHint);
    } catch (error) {
      setSwitchingOrg(false);
      message.error(
        error instanceof Error ? error.message : "Failed to switch organization",
      );
    }
  }

  async function onNext() {
    if (value === -1) onChange(0);
    const fields = PERSONAL_ITEMS[value]?.validate;
    // 这里是针对Image的一个逻辑，用户在点击 “Next” 时，若未上传Image，就给一个默认的
    if (fields && "Image" in fields && !form.getFieldValue("Image")) {
      form.setFieldValue("Image", AVATAR_DEFAULT);
    }
    if (fields) await form.validateFields(Object.values(fields)); // 按需校验 当前流程位置的表单数据
    if (value + 1 < PERSONAL_ITEMS?.length) {
      onChange(value + 1);
    } else {
      track(STATSIG.PERSONAL_SITE.INITIALIZE);
      onChange();
    }
  }

  function onBack() {
    onChange(value - 1);
  }

  const stepHint = bluerateHint
    ? {
        badge: "New Personal site",
        intro: `This creates a new Personal site for ${bluerateHint.currentOrgName}. Every organization has its own site and its own content, so what you enter here won't change your other sites.`,
      }
    : {};

  return value === -1 ? (
    bluerateHint ? (
      <div className="layout-profile-start">
        <BluerateSetupBanner
          hint={bluerateHint}
          loading={switchingOrg}
          onOpen={onOpenBluerateProfile}
        />
        <StartSetup onClick={onNext} />
      </div>
    ) : (
      <StartSetup onClick={onNext} />
    )
  ) : (
    <div className="layout-profile-info">
      {bluerateHint ? (
        <BluerateSetupBanner
          hint={bluerateHint}
          loading={switchingOrg}
          onOpen={onOpenBluerateProfile}
        />
      ) : null}
      <div className="layout-profile-info-main">
        <ul className="layout-profile-process">
          {PERSONAL_ITEMS.map((v, i) => {
            const IS_FILLED = i < value;
            const IS_FILLING = i === value;
            return (
              <li
                key={v.icon}
                className={`${IS_FILLED ? "layout-profile-process-filled" : ""} ${
                  IS_FILLING ? "layout-profile-process-filling" : ""
                }`}
              >
                <div className="layout-profile-process-icon">
                  <div>
                    <img src={IS_FILLED ? ICON_FILLED : v.icon} />
                  </div>
                  <div className="layout-profile-process-line" />
                </div>
                <div className="layout-profile-process-ctx">
                  <p>{v.title}</p>
                  <p>{v.description}</p>
                </div>
              </li>
            );
          })}
          {bluerateHint ? (
            <li className="layout-profile-process-bluerate">
              <div
                className="layout-profile-process-bluerate-avatar"
                style={{ background: bluerateHint.profileOrgColor }}
              >
                {bluerateHint.profileOrgShort}
              </div>
              <div className="layout-profile-process-bluerate-copy">
                <p>Bluerate profile</p>
                <p>Managed in {bluerateHint.profileOrgName}</p>
              </div>
              <Button
                type="link"
                loading={switchingOrg}
                onClick={onOpenBluerateProfile}
              >
                Open
              </Button>
            </li>
          ) : null}
        </ul>
        <div className="layout-profile-info-form">
          {value ? (
            <button onClick={onBack} className="layout-profile-process-back">
              {"<"} Back
            </button>
          ) : null}
          <div>
            {loading ? <Spin spinning size="small" /> : null}
            {PERSONAL_ITEMS?.map((v, i) => (
              <div
                key={v.icon}
                style={{ display: i === value ? "block" : "none" }}
              >
                {
                  <v.component
                    title={v.title}
                    badge={stepHint.badge}
                    intro={stepHint.intro}
                  />
                }
              </div>
            ))}
          </div>
          <Button
            type="primary"
            onClick={onNext}
            className="layout-profile-info-next"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

function BluerateSetupBanner({
  hint,
  loading,
  onOpen,
}: {
  hint: BluerateSetupHint;
  loading?: boolean;
  onOpen(): void;
}) {
  return (
    <div className="layout-profile-bluerate-banner">
      <span className="layout-profile-bluerate-banner-icon">i</span>
      <div className="layout-profile-bluerate-banner-copy">
        <p>You're setting up the Personal site for {hint.currentOrgName}</p>
        <p>
          You already have a Bluerate profile in {hint.profileOrgName}. Since
          each person has just one Bluerate profile, anything you change here
          won't affect it.
        </p>
        <Button type="link" loading={loading} onClick={onOpen}>
          {`Edit Bluerate profile in ${hint.profileOrgName} →`}
        </Button>
      </div>
    </div>
  );
}

export default ProfileConfig;
